/**
 * `/ebay` -- the connect, the confirmation gate, and the disconnect.
 *
 * DECIDES NOTHING ITSELF, and APPLIES nothing itself either. Every decision
 * (which state the connection is in, what a scope permits, whether a 409 is the
 * repair path) and every mutation (`*Action`) lives in lib/ebayView.ts and
 * lib/oauthLanding.ts, where a Node test can hold the collaborators -- the
 * STANDING RULE from the Task 7-11 reviews. What is left here is JSX and
 * component state.
 *
 * ── THE ONE RULE THIS FILE EXISTS TO KEEP, AND ITS HONEST LIMIT ─────────────
 * CONNECTING DOES NOT ENABLE `feature.ebay`. `oauth-finish` stores the
 * connection and deliberately writes no setting (api/src/adminRoutes.ts:
 * 1129-1189), so no automatic path from a consent to a running sync exists.
 * On THIS ROUTE the flag is turned on in exactly one place -- inside
 * `<EbayConfirm>` -- and `enableEbayAction` refuses it unless the attestation
 * was ticked. Nothing on the landing path touches the flag.
 *
 * WHAT THAT IS NOT (Task 12 review, MIN-1). It is NOT an enforcement boundary,
 * and describing it as one would mislead the next reader. `views/MasterData.tsx`
 * (`/stammdaten` -> Einstellungen) patches the SAME flag with no confirmation
 * and no account check, because D10 makes it self-serve -- so the SPA has two
 * `patchFeatures` call sites, of which this is the gated one. The server records
 * nothing about whether a human ever confirmed the account, so nothing
 * downstream can require it.
 *
 * WHAT THIS GATE ACTUALLY BUYS, stated so it can be relied on: no automatic
 * path from a connect to syncing; an honest disclosure at the moment of maximum
 * attention (the lured operator's screen denies readiness in the same breath as
 * reporting success, and says in the place an account name would go that we
 * cannot read it); and idempotence against a reload, because the gate is a
 * function of `GET /admin-api/ebay` rather than of the landing. THE ENFORCING
 * half of N3 is amendment A1's `browser_mismatch`, which fails closed on the
 * server; this is the residual disclosure for the case where a connection
 * nonetheless exists and nobody can name the account.
 *
 * WE DO NOT KNOW WHOSE eBAY ACCOUNT THIS IS, AND WE SAY SO. The three granted
 * scopes cannot read an account name, so `<EbayConfirm>` states that in the
 * place an account name would have gone and offers the check the operator can
 * actually make. There is deliberately no identity fetch here to mirror
 * views/Sheet.tsx's `<GoogleConfirm>`: an implied identity would be worse than
 * an admitted gap (plan 3c interface D13, amendment E1).
 *
 * TWO THINGS ARE RENDERED TOGETHER, ALWAYS, the same as views/Sheet.tsx:
 * <ConnectOutcome> (which paints nothing at all on an ordinary visit) and the
 * panel below it. Branching on the landing to choose between them would put a
 * classification decision back into JSX and would break the moment
 * `clearSearch` empties the query.
 *
 * THE CONNECT IS A TOP-LEVEL NAVIGATION, never a fetch (plan 3b amendment A1).
 * The whole argument is in lib/navigation.ts; this file must not acquire a
 * second way to leave the page.
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMe } from '../Boot';
import { useRoute } from '../Router';
import { ADMIN_API } from '../lib/api';
import { gateReason } from '../lib/capabilities';
import { guardedAction } from '../lib/gate';
import { t } from '../lib/i18n';
import {
  connectionIdentity, disconnectEbayAction, ebayViewModel, enableEbayAction, finishEbayConnect,
  requestedScopeLabels, startEbayConnect,
  type EbayConnectionDetail, type MutationShell,
} from '../lib/ebayView';
import { revocationLink, searchParamsFromLandingQuery } from '../lib/oauthLanding';
import { ebayQueryOptions } from '../queries';
import { ConnectOutcome } from '../components/ConnectOutcome';
import { ErrorNotice } from '../components/ErrorNotice';
import { PageBody, PageHeader } from '../components/ui/page';
import { Spinner } from '../components/Spinner';
import { StatusPill } from '../components/StatusPill';
import type { EbayFinish } from '../lib/types';

/** The panel-level busy/error pair every mutation-bearing panel shares, plus
 *  the re-entrancy gate -- the same shape views/Sheet.tsx and
 *  views/MasterData.tsx use. `gate` is the ref that refuses a double-click;
 *  `busy` is what the render shows (lib/gate.ts, final review I-2). */
function useMutationShell(): MutationShell & {
  busy: boolean;
  error: string | null;
  gate: { current: string | null };
} {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gate = useRef<string | null>(null);
  return { queryClient, setBusy, setError, busy, error, gate };
}

function PanelError({ message }: { message: string | null }) {
  if (message === null) return null;
  return <p className="ebay-error" role="alert">{message}</p>;
}

/**
 * eBay's own third-party-access page, rendered wherever an operator might
 * decide this connection is not theirs.
 *
 * THE URL IS NOT PRESENTED AS AUTHORITATIVE. eBay publishes no stable deep link
 * to the list, so lib/oauthLanding.ts's entry carries the navigation path as
 * well and this renders BOTH -- naming a URL that may 404 without also naming
 * the two clicks that get there would strand the one person who can revoke the
 * grant. `rel` belts index.html's `referrer=same-origin`.
 */
function RevokeLink() {
  const link = revocationLink('ebay');
  return (
    <p className="ebay-revoke">
      <a href={link.href} target="_blank" rel="noopener noreferrer">{link.label}</a>
      {link.hint === null ? null : <span className="ebay-revoke-hint">{link.hint}</span>}
    </p>
  );
}

function ScopeList({ scopes }: { scopes: string[] }) {
  return (
    <ul className="ebay-scopes">
      {scopes.map((label) => <li key={label}>{label}</li>)}
    </ul>
  );
}

// ── the landing hand-over ───────────────────────────────────────────────────

/**
 * <ConnectOutcome>'s success child, and it is deliberately NOT the
 * confirmation panel: the panel below is driven by `GET /admin-api/ebay`, so it
 * appears identically after a landing and after a reload, and the gate cannot
 * be skipped by refreshing the page.
 *
 * This line's whole job is to deny readiness in the same breath as reporting
 * success. `connected !== true` renders nothing at all -- the panel below reads
 * the server's own answer, and claiming a connect the server did not confirm is
 * the failure mode this view exists to avoid.
 */
function ConnectStored({ connected }: { connected: boolean }) {
  if (!connected) return null;
  return <p className="ebay-stored" role="status">{t('ebay.landing.stored')}</p>;
}

// ── the panels ──────────────────────────────────────────────────────────────

/**
 * WHAT IS ABOUT TO BE GRANTED, BEFORE THE REDIRECT. eBay grants the three
 * scopes together -- there is no per-scope consent -- so an owner who does not
 * want StackStock reading payout data has to find that out here, while
 * declining is still free.
 */
function ConnectPanel({ shell, notConfigured, onConnect }: {
  shell: ReturnType<typeof useMutationShell>;
  notConfigured: boolean;
  onConnect: () => void;
}) {
  return (
    <div className="ebay-panel ebay-panel-connect">
      <h2>{t('ebay.connect.title')}</h2>
      <PanelError message={shell.error} />
      <p>{t('ebay.connect.body')}</p>

      <h3>{t('ebay.connect.scopeTitle')}</h3>
      <ScopeList scopes={requestedScopeLabels()} />
      <p>{t('ebay.connect.scopeNote')}</p>

      <button type="button" disabled={shell.busy} onClick={onConnect}>
        {t('ebay.connect.submit')}
      </button>

      {/* A missing platform secret is the truthful state of the ENVIRONMENT
          (adminRoutes.ts:1121), not a fault the owner caused and not something
          a retry can fix -- so it is a named state, never an error toast. */}
      {notConfigured ? (
        <div className="ebay-not-configured" role="status">
          <h3>{t('ebay.notConfigured.title')}</h3>
          <p>{t('ebay.notConfigured.body')}</p>
        </div>
      ) : null}
    </div>
  );
}

/** `status === 'revoked'` (or anything else that is not `'active'`): the row
 *  survives a disconnect, so this is a real state rather than an absent one --
 *  and the way back is a fresh consent round, i.e. the same connect panel. */
function DisconnectedPanel({ shell, pill, note, notConfigured, onConnect }: {
  shell: ReturnType<typeof useMutationShell>;
  pill: { kind: 'ok' | 'warn' | 'error' | 'neutral'; label: string };
  note: string;
  notConfigured: boolean;
  onConnect: () => void;
}) {
  return (
    <div className="ebay-panel ebay-panel-disconnected">
      <h2>{t('ebay.disconnected.title')}</h2>
      <StatusPill kind={pill.kind} label={pill.label} />
      <p>{note}</p>
      <ConnectPanel shell={shell} notConfigured={notConfigured} onConnect={onConnect} />
    </div>
  );
}

/**
 * THE CONFIRMATION GATE (the N3 mitigation, plan 3c interface D13).
 *
 * Rendered for every active connection whose `feature.ebay` is still off, so it
 * is reached the same way after a fresh landing and after a reload. Four things
 * are on it, and the third is the one that matters:
 *
 *  1. WHAT WAS CONNECTED -- the tenant this connection landed in, the connect
 *     time, and the scopes the SERVER reports as granted (never the three this
 *     client asked for: a grant that came back different must look different).
 *  2. WHAT WE CANNOT TELL THEM -- the account NAME is unreadable under
 *     sell.fulfillment/sell.inventory/sell.finances, said plainly, followed by
 *     the check the operator can make at eBay themselves.
 *  3. AN EXPLICIT CONFIRM -- the checkbox gates the button, AND
 *     `enableEbayAction` re-checks it, so the gate does not live in a
 *     `disabled` attribute alone.
 *  4. THE OTHER OUTCOME -- the revocation link and a disconnect, for the
 *     operator who decides this is not their account.
 */
function EbayConfirm({ shell, detail, tenantName, confirmed, onConfirmedChange, onEnable, onDisconnect }: {
  shell: ReturnType<typeof useMutationShell>;
  detail: EbayConnectionDetail;
  tenantName: string;
  confirmed: boolean;
  onConfirmedChange: (confirmed: boolean) => void;
  onEnable: () => void;
  onDisconnect: () => void;
}) {
  return (
    <div className="ebay-panel ebay-panel-confirm">
      <h2>{t('ebay.confirm.title')}</h2>
      <StatusPill kind={detail.pill.kind} label={detail.pill.label} />
      <PanelError message={shell.error} />
      <p>{t('ebay.confirm.intro')}</p>

      <dl>
        <dt>{t('ebay.confirm.accountLabel')}</dt>
        <dd>
          <span className="ebay-confirm-unknown">{t('ebay.confirm.accountUnknown')}</span>
          <span className="ebay-confirm-check">{t('ebay.confirm.accountCheck')}</span>
        </dd>

        <dt>{t('ebay.confirm.tenantLabel')}</dt>
        <dd>{tenantName}</dd>

        <dt>{t('ebay.confirm.connectedAtLabel')}</dt>
        <dd>{detail.connectedAtLine}</dd>

        <dt>{t('ebay.scopeLabel')}</dt>
        <dd><ScopeList scopes={detail.scopes} /></dd>
      </dl>

      <label className="ebay-confirm-box">
        <input
          type="checkbox"
          checked={confirmed}
          disabled={shell.busy}
          onChange={(event) => onConfirmedChange(event.target.checked)}
        />
        {t('ebay.confirm.checkbox')}
      </label>
      <button type="button" disabled={shell.busy || !confirmed} onClick={onEnable}>
        {t('ebay.confirm.submit')}
      </button>

      <p>{t('ebay.confirm.notMine')}</p>
      <RevokeLink />
      <DisconnectControl shell={shell} onDisconnect={onDisconnect} />
    </div>
  );
}

/** The consequences are stated NEXT TO the button, not only inside the modal:
 *  a `window.confirm` is read by the person who already decided. */
function DisconnectControl({ shell, onDisconnect }: {
  shell: ReturnType<typeof useMutationShell>;
  onDisconnect: () => void;
}) {
  return (
    <div className="ebay-disconnect">
      <p>{t('ebay.disconnect.note')}</p>
      <button type="button" disabled={shell.busy} onClick={onDisconnect}>
        {t('ebay.disconnect.submit')}
      </button>
    </div>
  );
}

function ActivePanel({ shell, detail, onDisconnect }: {
  shell: ReturnType<typeof useMutationShell>;
  detail: EbayConnectionDetail;
  onDisconnect: () => void;
}) {
  return (
    <div className="ebay-panel ebay-panel-active">
      <h2>{t('ebay.active.title')}</h2>
      <StatusPill kind={detail.pill.kind} label={detail.pill.label} />
      <PanelError message={shell.error} />
      <p>{t('ebay.active.syncOn')}</p>
      <p>{detail.connectedAtLine}</p>
      <h3>{t('ebay.scopeLabel')}</h3>
      <ScopeList scopes={detail.scopes} />
      <RevokeLink />
      <DisconnectControl shell={shell} onDisconnect={onDisconnect} />
    </div>
  );
}

/**
 * MERKER e / amendment E3, and the honesty is the requirement.
 *
 * The pause is CLIENT-SIDE ONLY: `POST /admin-api/actions/ebay-sync` checks
 * `features.ebay` and never `tenant.status` (api/src/adminRoutes.ts:1598-1600),
 * so a past_due tenant reaching that endpoint another way still runs the job.
 * Saying "eBay ist gesperrt" would sell a guarantee the platform does not make;
 * this is the surface where the owner can actually see the billing state, so it
 * is also where the limit belongs.
 */
function BillingNotice() {
  return (
    <div className="ebay-billing" role="alert">
      <h2>{t('ebay.billing.title')}</h2>
      <p>{t('ebay.billing.body')}</p>
      <p>{t('ebay.billing.limit')}</p>
    </div>
  );
}

// ── the view ────────────────────────────────────────────────────────────────

export function Ebay() {
  const me = useMe();
  const { search, clearSearch } = useRoute();
  const shell = useMutationShell();
  const ebay = useQuery(ebayQueryOptions());
  const [notConfigured, setNotConfigured] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  /**
   * THE ATTESTATION BELONGS TO ONE GRANT (review MIN-2). `confirmed` is
   * component state and would otherwise survive a disconnect, a reconnect and
   * any other change to the connection row while this view stays mounted --
   * carrying one account's "ja, das ist mein Konto" onto a different one.
   * `connectionIdentity` is the pure decision (lib/ebayView.ts, Node-tested);
   * this is the two lines that apply it.
   */
  const identity = ebay.isSuccess ? connectionIdentity(ebay.data) : null;
  useEffect(() => { setConfirmed(false); }, [identity]);

  // TIER 1 all three (lib/gate.ts): the connect mints a state row and leaves
  // the page, the enable writes a feature flag that unlocks live sync buttons,
  // and the disconnect revokes a grant AT eBay. `handleDisconnect` is the one
  // the review named specifically -- it is externally visible and not
  // idempotent in any way the operator can see.
  function handleConnect() {
    guardedAction(shell.gate, 'ebayStart', () => startEbayConnect({
      start: ADMIN_API.ebayStart,
      setBusy: shell.setBusy,
      setError: shell.setError,
      setNotConfigured,
      queryClient: shell.queryClient,
    }));
  }

  function handleEnable() {
    guardedAction(shell.gate, 'patchFeatures', () => enableEbayAction(
      confirmed,
      { ...shell, patchFeatures: ADMIN_API.patchFeatures },
    ));
  }

  function handleDisconnect() {
    guardedAction(shell.gate, 'ebayDisconnect', () => disconnectEbayAction({
      ...shell,
      confirm: (message) => window.confirm(message),
      disconnect: ADMIN_API.ebayDisconnect,
      readEbay: ADMIN_API.getEbay,
      setNotice,
    }));
  }

  /** A plain call, not a nested `<Panel />` component: a component DECLARED
   *  inside a render is a new type on every render, and React would remount
   *  the panel -- and drop the confirmation checkbox -- on every keystroke
   *  elsewhere in the tree (views/Sheet.tsx's own `renderPanel`). */
  function renderPanel(tenantName: string): ReactNode {
    if (ebay.isPending) return <Spinner />;
    if (ebay.isError) {
      return (
        <div className="ebay-panel">
          <ErrorNotice error={ebay.error} />
          <button type="button" onClick={() => void ebay.refetch()}>{t('blocking.retry')}</button>
        </div>
      );
    }
    const model = ebayViewModel(ebay.data);
    switch (model.kind) {
      case 'never':
        return <ConnectPanel shell={shell} notConfigured={notConfigured} onConnect={handleConnect} />;
      case 'disconnected':
        return (
          <DisconnectedPanel
            shell={shell}
            pill={model.pill}
            note={model.note}
            notConfigured={notConfigured}
            onConnect={handleConnect}
          />
        );
      case 'confirm':
        return (
          <EbayConfirm
            shell={shell}
            detail={model.detail}
            tenantName={tenantName}
            confirmed={confirmed}
            onConfirmedChange={setConfirmed}
            onEnable={handleEnable}
            onDisconnect={handleDisconnect}
          />
        );
      case 'active':
        return <ActivePanel shell={shell} detail={model.detail} onDisconnect={handleDisconnect} />;
    }
  }

  // Boot resolves `me` before any view mounts, so this is a cache read.
  if (!me.isSuccess) return <Spinner />;

  // `capabilities.ts` is CONSUMED, never re-derived: the reason order (role,
  // then feature, then billing) is its own, and a `status === 'past_due'` test
  // written here would claim "eBay pausiert" for a tenant whose eBay was never
  // switched on. `features` comes from this view's own query rather than from
  // `me`, because it is the copy this view is about to change.
  const paused = ebay.isSuccess && gateReason('ebayActions', {
    role: me.data.role,
    status: me.data.tenant.status,
    features: ebay.data.features,
  }) === 'billing';

  return (
    <section className="ebay-view">
      <PageBody>
      <PageHeader title={t('nav.ebay')} />
      {paused ? <BillingNotice /> : null}
      {notice === null ? null : <p className="ebay-notice" role="status">{notice}</p>}

      <ConnectOutcome<EbayFinish>
        provider="ebay"
        search={searchParamsFromLandingQuery(search)}
        clearSearch={clearSearch}
        onRetry={handleConnect}
        onFinish={(state) => finishEbayConnect(state, {
          queryClient: shell.queryClient,
          ebayFinish: ADMIN_API.ebayFinish,
        })}
      >
        {(result) => <ConnectStored connected={result.connected === true} />}
      </ConnectOutcome>

      {renderPanel(me.data.tenant.name)}
      </PageBody>
    </section>
  );
}
